// VOD API service for managing VOD assets (highlights and condensed matches)

import { api } from './api'
import type { VodAsset, VodAssetsResponse, VodAssetFilters } from '../types/vod'

/**
 * Fetch list of VOD assets with optional filters
 */
export const fetchVodAssets = (filters?: VodAssetFilters): Promise<VodAssetsResponse> => {
  const apiParams: Record<string, any> = {}
  
  if (filters?.vod_type_code) apiParams.vod_type_code = filters.vod_type_code
  if (filters?.status) apiParams.status = filters.status
  if (filters?.publish_status) apiParams.publish_status = filters.publish_status
  if (filters?.start_date) apiParams.start_date = filters.start_date
  if (filters?.end_date) apiParams.end_date = filters.end_date
  if (filters?.search?.trim()) apiParams.search = filters.search.trim()
  if (filters?.match_id) apiParams.match_id = filters.match_id
  if (filters?.sort_order) apiParams.sort_order = filters.sort_order
  if (filters?.page) apiParams.page = filters.page
  if (filters?.limit) apiParams.limit = filters.limit

  return api.get<VodAssetsResponse>('/vod/assets', apiParams)
}

/**
 * Fetch a single VOD asset by ID
 */
export const fetchVodAsset = (assetId: string): Promise<VodAsset> => {
  return api.get<VodAsset>(`/vod/assets/${assetId}`)
}

/**
 * Update a VOD asset (title, description, and synopsis)
 */
export const updateVodAsset = (
  assetId: string,
  data: { tc_title?: string; tc_description?: string; tc_synopsis?: string }
): Promise<{ message: string; asset: VodAsset }> => {
  return api.put(`/vod/assets/${assetId}`, data)
}

/**
 * Get all tags for a VOD asset
 */
export const fetchVodAssetTags = (assetId: string): Promise<{ tags: string[] }> => {
  return api.get(`/vod/assets/${assetId}/tags`)
}

/**
 * Set tags for a VOD asset
 */
export const updateVodAssetTags = (
  assetId: string,
  tags: string[]
): Promise<{ message: string; tags: string[] }> => {
  return api.put(`/vod/assets/${assetId}/tags`, { tags })
}

/**
 * Get all available VOD tags (for dropdown options)
 */
export const fetchVodTags = (): Promise<{ 
  tags: Array<{ id: string; value: string; label: string }> 
}> => {
  return api.get('/vod/tags')
}

/**
 * Create a new VOD tag
 */
export const createVodTag = (
  tagKey: string,
  displayName?: string
): Promise<{ tag: { id: string; value: string; label: string } }> => {
  return api.post('/vod/tags', { 
    tag_key: tagKey, 
    display_name: displayName || tagKey 
  })
}

